import { FormsV1 } from '@frontend/api-digital-forms';
import { http } from '@frontend/fetch';
import { DEFAULT_DFX_HTTP_OPTIONS } from '../../constants';

export type DraftAvailabilityStatus =
  | 'disabled'
  | 'loading'
  | 'ineligible'
  | 'eligible-no-draft'
  | 'eligible-has-draft'
  /** Eligibility request failed: autosave cannot be offered, but the form stays usable. */
  | 'eligibility-unknown'
  /** Existence request failed: a draft may exist, so new writes must not overwrite it (spec FR-01). */
  | 'existence-unknown';

interface UseDraftAvailabilityArgs {
  companyId: string;
  submissionId: string;
  /** Only check once the template is loaded and the submission is still fillable. */
  enabled: boolean;
}

const isNonRetryableError = (error: unknown) => {
  if (!http.isHttpError(error)) return false;
  const status = error.status ?? 0;
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
};

const QUERY_OPTIONS = {
  cacheTime: 0,
  staleTime: 0,
  refetchOnWindowFocus: false,
  retry: (failureCount: number, error: unknown) => failureCount < 2 && !isNonRetryableError(error),
};

/** Checks autosave eligibility and draft existence in parallel. A failed request is never treated as "no draft". */
export const useDraftAvailability = ({ companyId, submissionId, enabled }: UseDraftAvailabilityArgs) => {
  const request = { companyId, submissionId };
  const isEnabled = enabled && !!companyId && !!submissionId;

  const eligibility = FormsV1.Queries.useIsAutoSavePossibleQuery({
    request,
    options: { ...QUERY_OPTIONS, enabled: isEnabled },
    httpOptions: DEFAULT_DFX_HTTP_OPTIONS,
  });

  const existence = FormsV1.Queries.useIsDraftExistsQuery({
    request,
    options: { ...QUERY_OPTIONS, enabled: isEnabled },
    httpOptions: DEFAULT_DFX_HTTP_OPTIONS,
  });

  const getStatus = (): DraftAvailabilityStatus => {
    if (!isEnabled) return 'disabled';
    if (eligibility.isError) return 'eligibility-unknown';
    if (!eligibility.data) return 'loading';
    if (!eligibility.data.autoSavePossible) return 'ineligible';
    if (existence.isError) return 'existence-unknown';
    if (!existence.data) return 'loading';
    return existence.data.draftExists ? 'eligible-has-draft' : 'eligible-no-draft';
  };

  const refetch = async () => {
    await Promise.all([eligibility.refetch(), existence.refetch()]);
  };

  return { status: getStatus(), refetch, isRefetching: eligibility.isFetching || existence.isFetching };
};
